import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { ENABLE_DAMPING, DAMPING_FACTOR } from '@config/constants.js';

/**
 * CameraControls - Wrapper for OrbitControls with card-specific configuration
 *
 * Responsibilities:
 * - Set up OrbitControls for card manipulation
 * - Configure rotation, zoom, and pan limits
 * - Enable smooth damping for better UX
 * - Prevent unnatural camera angles
 */
export class CameraControls {
  constructor(camera, domElement) {
    if (!camera) {
      throw new Error('CameraControls requires a camera');
    }
    if (!domElement) {
      throw new Error('CameraControls requires a DOM element');
    }

    this.camera = camera;
    this.domElement = domElement;
    this.controls = null;

    this._initialize();
  }

  /**
   * Initialize OrbitControls with card-specific settings
   * @private
   */
  _initialize() {
    this.controls = new OrbitControls(this.camera, this.domElement);

    // Enable damping for smooth rotation
    this.controls.enableDamping = ENABLE_DAMPING;
    this.controls.dampingFactor = DAMPING_FACTOR;

    // Rotation settings
    // Allow full horizontal rotation
    this.controls.minAzimuthAngle = -Infinity;
    this.controls.maxAzimuthAngle = Infinity;

    // Limit vertical rotation to prevent upside-down view
    // Prevents camera from going below or above the card
    this.controls.minPolarAngle = Math.PI / 4; // 45 degrees from top
    this.controls.maxPolarAngle = (3 * Math.PI) / 4; // 45 degrees from bottom

    // Zoom settings - keep card visible
    this.controls.minDistance = 1.5; // Minimum zoom (closer)
    this.controls.maxDistance = 10; // Maximum zoom (farther)

    // Pan settings - disable panning to keep card centered
    this.controls.enablePan = false;

    // Enable zoom with mouse wheel
    this.controls.enableZoom = true;
    this.controls.zoomSpeed = 1.0;

    // Rotation speed
    this.controls.rotateSpeed = 0.5;

    // Set the target to the center of the scene (0, 0, 0)
    this.controls.target.set(0, 0, 0);

    // Update controls to apply settings
    this.controls.update();
  }

  /**
   * Update controls (called each frame)
   * Required when damping is enabled
   */
  update() {
    if (this.controls) {
      this.controls.update();
    }
  }

  /**
   * Enable or disable controls
   * @param {boolean} enabled - Whether controls should be enabled
   */
  setEnabled(enabled) {
    this.controls.enabled = enabled;
  }

  /**
   * Reset camera to initial position
   */
  reset() {
    this.controls.reset();
  }

  /**
   * Enable or disable auto-rotation
   * @param {boolean} enabled - Whether auto-rotation should be enabled
   * @param {number} speed - Rotation speed (default: 2.0)
   */
  setAutoRotate(enabled, speed = 2.0) {
    this.controls.autoRotate = enabled;
    this.controls.autoRotateSpeed = speed;
  }

  /**
   * Get the underlying OrbitControls instance
   * @returns {OrbitControls}
   */
  getControls() {
    return this.controls;
  }

  /**
   * Set the target point the camera looks at
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @param {number} z - Z coordinate
   */
  setTarget(x, y, z) {
    this.controls.target.set(x, y, z);
    this.controls.update();
  }

  /**
   * Enable or disable damping
   * @param {boolean} enabled - Whether damping should be enabled
   * @param {number} factor - Damping factor (0-1, default from constants)
   */
  setDamping(enabled, factor = DAMPING_FACTOR) {
    this.controls.enableDamping = enabled;
    this.controls.dampingFactor = factor;
  }

  /**
   * Set zoom limits
   * @param {number} min - Minimum zoom distance
   * @param {number} max - Maximum zoom distance
   */
  setZoomLimits(min, max) {
    this.controls.minDistance = min;
    this.controls.maxDistance = max;
  }

  /**
   * Enable or disable zoom
   * @param {boolean} enabled - Whether zoom should be enabled
   */
  setZoomEnabled(enabled) {
    this.controls.enableZoom = enabled;
  }

  /**
   * Enable or disable rotation
   * @param {boolean} enabled - Whether rotation should be enabled
   */
  setRotationEnabled(enabled) {
    this.controls.enableRotate = enabled;
  }

  /**
   * Clean up resources and event listeners
   */
  dispose() {
    if (this.controls) {
      this.controls.dispose();
      this.controls = null;
    }

    this.camera = null;
    this.domElement = null;
  }
}
